'use strict';

const libPictProvider = require('pict-provider');

/**
 * RoleCloneProvider — the data layer for "clone a role". Deep-copies an existing Role into a new,
 * independent one the admin can then diverge freely: the Role row, its RolePermission grants, and its
 * RoleEdge children (the composition that makes it inherit other roles/groups). The clone shares no rows
 * with the source.
 *
 * What is copied (mirrors the documented role model — Role / Role Permission / Role Edge):
 *   - Role: a new POST Role (IDRole/GUID dropped), Name from the admin, everything else from the source.
 *   - Grants: every RolePermission on the source, re-created against the new IDRole. A role's direct grants
 *     live in its membership STORAGE scope (global role -> CustomerMembership/0; customer role ->
 *     Customer/<IDCustomer>), so when the clone lands in a different customer than the source (localising a
 *     global template into a customer), those membership-scope grants are remapped to the new role's scope;
 *     deeper explicit-context grants are copied verbatim.
 *   - Edges: every RoleEdge where the source is the parent, re-created with the new role as parent.
 *
 * What is NOT touched: user assignments (a fresh role has no members yet — hand it out afterwards) and
 * project-type scoping (no such axis exists in the permission model today — a scoped variant is a follow-up).
 *
 * The copy is client-orchestrated (one create per record, no server transaction), so partial failures are
 * possible; the result reports per-record Succeeded/Failed so the admin can see exactly what landed.
 * State lives in pict.AppData.RoleClone; the RoleClone view derives a clean render-state from it.
 */
class RoleCloneProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	onAfterInitialize()
	{
		this._ensureState();
		return super.onAfterInitialize();
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get idCustomer()
	{
		return Number(this.currentUser.CustomerID) || 0;
	}

	/** Customer-1 / role-5 super-user, or the SuperAdminAccessRole (mirrors AccessWorkspace-Provider). */
	get isSuperUser()
	{
		const tmpUser = this.currentUser;
		if ((Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1))
		{
			return true;
		}
		const tmpRoles = tmpUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && pRole.Hash === 'SuperAdminAccessRole');
	}

	/** Holds a RoleManager/SuperAdminUser role, or is a super-user — the gate for creating/editing roles. */
	get isRoleManager()
	{
		if (this.isSuperUser)
		{
			return true;
		}
		const tmpRoles = this.currentUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && (pRole.PermissionLevel === 'RoleManager' || pRole.PermissionLevel === 'SuperAdminUser'));
	}

	// --- REST helpers (promise wrappers over the Headlight rest client) ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/** Read an entire filtered set (Count then one page sized to it) — mirrors AccessWorkspace._getFilteredAll. */
	async _getFilteredAll(pEntitySet, pFilter, pCap)
	{
		const tmpCap = pCap || 1000;
		let tmpTotal = tmpCap;
		try
		{
			const tmpCount = await this._getJSON(`${pEntitySet}/Count/FilteredTo/${pFilter}`);
			tmpTotal = Math.min((tmpCount && tmpCount.Count) || 0, tmpCap);
		}
		catch (pError)
		{
			tmpTotal = tmpCap;
		}
		if (!tmpTotal)
		{
			return [];
		}
		const tmpRows = await this._getJSON(`${pEntitySet}/FilteredTo/${pFilter}/0/${tmpTotal}`);
		return Array.isArray(tmpRows) ? tmpRows : [];
	}

	// --- State ---

	_ensureState()
	{
		if (!this.pict.AppData.RoleClone)
		{
			this.pict.AppData.RoleClone =
			{
				SourceIDRole: 0,
				SourceRole: null,           // the loaded source Role record
				RoleOptions: [],            // [{ Value, Label, Type, IDCustomer }]
				RolesLoaded: false,
				RolesLoading: false,
				IncludeGrants: true,
				IncludeEdges: true,
				NewName: '',
				TargetIDCustomer: null,     // number; null until defaulted from the source/session
				Preview: null,              // { GrantCount, EdgeCount }
				PreviewLoading: false,
				Loading: false,
				Result: null,               // { NewIDRole, Succeeded, Failed, Details:[{ Kind, Label, OK, Error? }] }
				Message: '',
			};
		}
		return this.pict.AppData.RoleClone;
	}

	get state()
	{
		return this._ensureState();
	}

	/** The role's grant STORAGE scope (mirrors PermissionMatrix-Provider._scope for the Customer context). */
	_membershipScope(pRole)
	{
		const tmpRoleCustomer = Number((pRole && pRole.IDCustomer) || 0);
		return (tmpRoleCustomer === 0)
			? { Context: 'CustomerMembership', IDContext: 0 }
			: { Context: 'Customer', IDContext: tmpRoleCustomer };
	}

	/** The effective target customer for the clone (defaults to the source's customer, else the session's). */
	_targetCustomer()
	{
		const tmpState = this.state;
		if (tmpState.TargetIDCustomer !== null && tmpState.TargetIDCustomer !== '' && !Number.isNaN(Number(tmpState.TargetIDCustomer)))
		{
			return Number(tmpState.TargetIDCustomer);
		}
		if (tmpState.SourceRole)
		{
			return Number(tmpState.SourceRole.IDCustomer || 0);
		}
		return this.idCustomer;
	}

	// --- Load ---

	/**
	 * Load the roles + permission groups the admin can clone from (global + this customer, not deleted).
	 * Concurrency-safe: the view auto-loads on render and the deep-link preselect also loads, so both share one
	 * in-flight request rather than racing (which would leave selectSourceAsync with an empty option list).
	 * @return {Promise<void>}
	 */
	loadRolesAsync()
	{
		if (this._rolesLoadPromise)
		{
			return this._rolesLoadPromise;
		}
		this._rolesLoadPromise = this._loadRolesInnerAsync();
		this._rolesLoadPromise.then(() => { this._rolesLoadPromise = null; }, () => { this._rolesLoadPromise = null; });
		return this._rolesLoadPromise;
	}

	async _loadRolesInnerAsync()
	{
		const tmpState = this.state;
		tmpState.RolesLoading = true;
		tmpState.Message = '';
		try
		{
			const tmpRoles = await this._getFilteredAll('Roles', `FBL~IDCustomer~INN~0,${this.idCustomer}~FBV~Deleted~EQ~0`, 1000);
			tmpRoles.sort((pA, pB) => String(pA.Name || '').localeCompare(String(pB.Name || '')));
			tmpState.RoleOptions = tmpRoles.map((pRole) => (
				{
					Value: Number(pRole.IDRole),
					Label: this._roleLabel(pRole),
					Type: pRole.Type || 'Role',
					IDCustomer: Number(pRole.IDCustomer || 0),
					Record: pRole,
				}));
			tmpState.RolesLoaded = true;
		}
		catch (pError)
		{
			tmpState.Message = `Could not load roles: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpState.RolesLoading = false;
	}

	_roleLabel(pRole)
	{
		const tmpKind = (pRole.Type === 'PermissionGroup') ? 'Permission Group' : (pRole.Type === 'UserGroup') ? 'User Group' : 'Role';
		const tmpScope = (Number(pRole.IDCustomer || 0) === 0) ? ' · global' : '';
		return `${pRole.Name || `Role ${pRole.IDRole}`} (${tmpKind}${tmpScope})`;
	}

	// --- Source-role picker (server-side search; replaces the capped preloaded <select>) ---

	/**
	 * (Re)mount the searchable source-role picker into #RoleClone-SourcePicker. Called from the view's onAfterRender,
	 * so it re-creates into the freshly-rendered mount each repaint (RoleClone re-renders its whole wrap on every
	 * action) — the selection is preserved via the ValueAddress binding + ResolveValue, mirroring ProjectEditWorkspace.
	 */
	mountSourcePicker()
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPickerProvider || (typeof tmpPickerProvider.createPicker !== 'function')) { return; }
		const tmpMount = '#RoleClone-SourcePicker';
		if (this.pict.ContentAssignment.getElement(tmpMount).length === 0) { return; }
		const tmpView = this.pict.views.RoleClone;
		const tmpPicker = tmpPickerProvider.createPicker('RoleClone-SourcePicker',
			{
				DestinationAddress: tmpMount,
				ValueAddress: 'AppData.RoleClone.SourceIDRole',
				Searchable: true,
				AllowClear: true,
				ClearLabel: '— choose a role to clone —',
				Placeholder: 'Search a role or permission group…',
				DataProvider: this._roleDataProvider(),
				ResolveValue: this._roleResolveValue(),
				OnChange: (pValue) => { this.selectSourceAsync(pValue).then(() => { if (tmpView) { tmpView.render(); } }); },
			});
		if (tmpPicker && (typeof tmpPicker.render === 'function')) { tmpPicker.render(); }
	}

	/** Server-side search DataProvider: Roles by Name LIKE, global (0) + this customer, not deleted, paged. */
	_roleDataProvider()
	{
		const tmpPageSize = 25;
		return (pSearchTerm, pPage) =>
		{
			const tmpStart = (Number(pPage) || 0) * tmpPageSize;
			let tmpFilter = `FBL~IDCustomer~INN~0,${this.idCustomer}~FBV~Deleted~EQ~0`;
			const tmpTerm = String(pSearchTerm == null ? '' : pSearchTerm).trim();
			if (tmpTerm) { tmpFilter = `FBV~Name~LK~${encodeURIComponent(`%${tmpTerm}%`)}~${tmpFilter}`; }
			return this._getJSON(`Roles/FilteredTo/${tmpFilter}/${tmpStart}/${tmpPageSize}`)
				.then((pRows) =>
				{
					const tmpList = Array.isArray(pRows) ? pRows : [];
					return { results: tmpList.map((pRole) => ({ Value: Number(pRole.IDRole), Text: this._roleLabel(pRole), Record: pRole })), hasMore: (tmpList.length >= tmpPageSize) };
				})
				.catch(() => ({ results: [], hasMore: false }));
		};
	}

	/** Resolve a preselected IDRole → {Value,Text} so the current selection shows its label on (re)mount. */
	_roleResolveValue()
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			return this._getJSON(`Role/${tmpID}`)
				.then((pRole) => ((pRole && pRole.IDRole) ? { Value: Number(pRole.IDRole), Text: this._roleLabel(pRole) } : { Value: tmpID, Text: `#${tmpID}` }))
				.catch(() => ({ Value: tmpID, Text: `#${tmpID}` }));
		};
	}

	/** Choose the source role by id: stash the record, default the new name + target customer, load the preview. */
	async selectSourceAsync(pIDRole)
	{
		const tmpState = this.state;
		const tmpID = parseInt(pIDRole, 10) || 0;
		tmpState.SourceIDRole = tmpID;
		tmpState.Result = null;
		tmpState.Message = '';
		const tmpOption = (tmpState.RoleOptions || []).find((pOption) => pOption.Value === tmpID);
		tmpState.SourceRole = tmpOption ? tmpOption.Record : null;
		// The source picker is server-side search (not a preloaded option list), so a chosen role won't be in
		// RoleOptions — fetch the record directly by id.
		if (!tmpState.SourceRole && tmpID)
		{
			tmpState.SourceRole = await this._getJSON(`Role/${tmpID}`).catch(() => null);
		}
		if (tmpState.SourceRole)
		{
			tmpState.NewName = `Copy of ${tmpState.SourceRole.Name || ''}`.trim();
			tmpState.TargetIDCustomer = Number(tmpState.SourceRole.IDCustomer || 0);
			// A customer role-manager can only create in their own customer — pin the target there.
			if (!this.isSuperUser)
			{
				tmpState.TargetIDCustomer = this.idCustomer;
			}
			await this.loadPreviewAsync();
		}
		else
		{
			tmpState.Preview = null;
		}
	}

	/** Preselect a source (from the Access "Duplicate" deep-link). selectSourceAsync resolves the record by id itself
	 * (and the picker's ResolveValue shows its label), so no preloaded role list is needed. */
	async preselectSourceAsync(pIDRole)
	{
		await this.selectSourceAsync(pIDRole);
	}

	/** Count what would be copied so the admin sees the size of the clone before running it. */
	async loadPreviewAsync()
	{
		const tmpState = this.state;
		if (!tmpState.SourceRole)
		{
			tmpState.Preview = null;
			return;
		}
		tmpState.PreviewLoading = true;
		try
		{
			const tmpGrants = await this._readGrantsAsync(tmpState.SourceRole);
			const tmpEdges = await this._readEdgesAsync(tmpState.SourceRole);
			tmpState.Preview = { GrantCount: tmpGrants.length, EdgeCount: tmpEdges.length };
		}
		catch (pError)
		{
			tmpState.Message = `Could not read the source role's grants: ${pError && pError.message ? pError.message : pError}`;
			tmpState.Preview = null;
		}
		tmpState.PreviewLoading = false;
	}

	_readGrantsAsync(pRole)
	{
		return this._getFilteredAll('RolePermissions', `FBV~IDRole~EQ~${Number(pRole.IDRole)}`, 5000);
	}

	_readEdgesAsync(pRole)
	{
		return this._getFilteredAll('RoleEdges', `FBV~ParentIDRole~EQ~${Number(pRole.IDRole)}~FBV~Deleted~EQ~0`, 2000);
	}

	// --- Field setters (from the view) ---

	setField(pField, pValue)
	{
		this.state[pField] = pValue;
	}

	toggleInclude(pField)
	{
		this.state[pField] = !this.state[pField];
	}

	// --- Apply ---

	/**
	 * Run the clone: create the new Role, then re-create its selected grants + edges against the new IDRole.
	 * Aggregates a per-record result. If the Role itself cannot be created nothing else is attempted.
	 * @return {Promise<object|null>} the result summary (also stashed on state.Result)
	 */
	async cloneAsync()
	{
		const tmpState = this.state;
		tmpState.Message = '';
		tmpState.Result = null;

		if (!this.isRoleManager)
		{
			tmpState.Message = 'You do not have permission to create roles.';
			return null;
		}
		const tmpSource = tmpState.SourceRole;
		if (!tmpSource)
		{
			tmpState.Message = 'Choose a source role to clone.';
			return null;
		}
		const tmpName = String(tmpState.NewName || '').trim();
		if (!tmpName)
		{
			tmpState.Message = 'Enter a name for the new role.';
			return null;
		}
		const tmpTargetCustomer = this._targetCustomer();
		if (!this.isSuperUser && tmpTargetCustomer !== this.idCustomer)
		{
			tmpState.Message = 'You can only create roles in your own customer.';
			return null;
		}

		tmpState.Loading = true;
		const tmpDetails = [];

		// 1) The Role row.
		let tmpNewID = 0;
		try
		{
			const tmpSaved = await this._postJSON('Role',
				{
					Name: tmpName,
					Type: tmpSource.Type || 'Role',
					PermissionLevel: tmpSource.PermissionLevel || '',
					Description: tmpSource.Description || '',
					IDCustomer: tmpTargetCustomer,
				});
			tmpNewID = Number(tmpSaved && tmpSaved.IDRole) || 0;
			if (!tmpNewID)
			{
				throw new Error('the server did not return a new role id');
			}
			tmpDetails.push({ Kind: 'Role', Label: `Role “${tmpName}” (#${tmpNewID})`, OK: true });
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not create the role: ${pError && pError.message ? pError.message : pError}`;
			return null;
		}

		// 2) Grants.
		if (tmpState.IncludeGrants)
		{
			const tmpSrcScope = this._membershipScope(tmpSource);
			const tmpTgtScope = this._membershipScope({ IDCustomer: tmpTargetCustomer });
			let tmpGrants = [];
			try
			{
				tmpGrants = await this._readGrantsAsync(tmpSource);
			}
			catch (pError)
			{
				tmpDetails.push({ Kind: 'Grants', Label: 'Read grants', OK: false, Error: String(pError && pError.message ? pError.message : pError) });
			}
			const tmpSeen = {};
			for (let i = 0; i < tmpGrants.length; i++)
			{
				const tmpGrant = tmpGrants[i];
				let tmpContext = tmpGrant.Context;
				let tmpIDContext = Number(tmpGrant.IDContext || 0);
				// Membership-scope grants follow the role to its new customer; deeper grants stay put.
				if (tmpContext === tmpSrcScope.Context && tmpIDContext === tmpSrcScope.IDContext)
				{
					tmpContext = tmpTgtScope.Context;
					tmpIDContext = tmpTgtScope.IDContext;
				}
				const tmpKey = `${tmpGrant.Hash}|${tmpContext}|${tmpIDContext}|${tmpGrant.Allowed}`;
				if (tmpSeen[tmpKey]) { continue; }
				tmpSeen[tmpKey] = true;
				try
				{
					/* eslint-disable no-await-in-loop */
					await this._postJSON('RolePermission',
						{
							IDRole: tmpNewID,
							Hash: tmpGrant.Hash,
							Allowed: tmpGrant.Allowed,
							Context: tmpContext,
							IDContext: tmpIDContext,
							IDCustomer: tmpTargetCustomer,
						});
					/* eslint-enable no-await-in-loop */
					tmpDetails.push({ Kind: 'Grant', Label: `${tmpGrant.Allowed ? 'grant' : 'deny'} ${tmpGrant.Hash}`, OK: true });
				}
				catch (pError)
				{
					tmpDetails.push({ Kind: 'Grant', Label: `${tmpGrant.Allowed ? 'grant' : 'deny'} ${tmpGrant.Hash}`, OK: false, Error: String(pError && pError.message ? pError.message : pError) });
				}
			}
		}

		// 3) Edges (composition — the source's inherited roles/groups).
		if (tmpState.IncludeEdges)
		{
			let tmpEdges = [];
			try
			{
				tmpEdges = await this._readEdgesAsync(tmpSource);
			}
			catch (pError)
			{
				tmpDetails.push({ Kind: 'Edges', Label: 'Read edges', OK: false, Error: String(pError && pError.message ? pError.message : pError) });
			}
			const tmpSeenChild = {};
			for (let i = 0; i < tmpEdges.length; i++)
			{
				const tmpChildID = Number(tmpEdges[i].ChildIDRole);
				if (!tmpChildID || tmpChildID === tmpNewID || tmpSeenChild[tmpChildID]) { continue; }
				tmpSeenChild[tmpChildID] = true;
				try
				{
					/* eslint-disable no-await-in-loop */
					await this._postJSON('RoleEdge',
						{ ParentIDRole: tmpNewID, ChildIDRole: tmpChildID, IDCustomer: tmpTargetCustomer });
					/* eslint-enable no-await-in-loop */
					tmpDetails.push({ Kind: 'Edge', Label: `includes role #${tmpChildID}`, OK: true });
				}
				catch (pError)
				{
					tmpDetails.push({ Kind: 'Edge', Label: `includes role #${tmpChildID}`, OK: false, Error: String(pError && pError.message ? pError.message : pError) });
				}
			}
		}

		tmpState.Result =
		{
			NewIDRole: tmpNewID,
			NewName: tmpName,
			Succeeded: tmpDetails.filter((pDetail) => pDetail.OK).length,
			Failed: tmpDetails.filter((pDetail) => !pDetail.OK).length,
			Details: tmpDetails,
		};
		tmpState.Loading = false;
		return tmpState.Result;
	}

	// --- Derive view state ---

	deriveRenderState()
	{
		const tmpState = this.state;

		tmpState.RoleOptionRows = (tmpState.RoleOptions || []).map((pOption) => (
			{
				Value: pOption.Value,
				Label: pOption.Label,
				SelectedAttr: (Number(pOption.Value) === Number(tmpState.SourceIDRole)) ? 'selected' : '',
			}));

		const tmpHasSource = !!tmpState.SourceRole;
		const tmpTargetCustomer = this._targetCustomer();
		const tmpName = String(tmpState.NewName || '').trim();
		const tmpCanApply = tmpHasSource && !!tmpName && this.isRoleManager && !tmpState.Loading;

		tmpState.HasSource = tmpHasSource;
		tmpState.SourceTypeLabel = tmpHasSource ? this._roleLabel(tmpState.SourceRole) : '';
		tmpState.TargetCustomerValue = tmpTargetCustomer;
		tmpState.TargetCustomerReadOnly = this.isSuperUser ? '' : 'readonly';
		tmpState.IncludeGrantsAttr = tmpState.IncludeGrants ? 'checked' : '';
		tmpState.IncludeEdgesAttr = tmpState.IncludeEdges ? 'checked' : '';
		tmpState.NewNameValue = tmpState.NewName || '';
		tmpState.ApplyDisabledAttr = tmpCanApply ? '' : 'disabled';

		// Conditional slots (single-element-array trick).
		tmpState.NoPermSlot = this.isRoleManager ? [] : [ {} ];
		tmpState.LoadingSlot = tmpState.Loading ? [ {} ] : [];
		tmpState.MessageSlot = tmpState.Message ? [ { Message: tmpState.Message } ] : [];

		let tmpPreviewText = '';
		if (tmpState.PreviewLoading)
		{
			tmpPreviewText = 'Reading source…';
		}
		else if (tmpState.Preview)
		{
			tmpPreviewText = `${tmpState.Preview.GrantCount} grant(s) · ${tmpState.Preview.EdgeCount} included role(s)`;
		}
		tmpState.PreviewSlot = (tmpHasSource && tmpPreviewText) ? [ { Text: tmpPreviewText } ] : [];

		tmpState.ResultSlot = tmpState.Result ? [ tmpState.Result ] : [];
		tmpState.ResultRows = tmpState.Result ? tmpState.Result.Details.map((pDetail) => (
			{
				Label: pDetail.Label,
				StatusClass: pDetail.OK ? 'has-text-success' : 'has-text-danger',
				StatusText: pDetail.OK ? 'created' : ('failed: ' + (pDetail.Error || 'error')),
			})) : [];

		return tmpState;
	}
}

module.exports = RoleCloneProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'RoleCloneProvider',
	AutoInitialize: true,
	AutoInitializeOrdinal: 42,
};
